import {
    Box,
    Button,
    Typography,
    useMediaQuery,
    TextField,
    Dialog,
} from "@mui/material";
import { ArrowLeft, FileText, CheckCircle, XCircle, X } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useState } from "react";

const isPdf = (file) => file?.type === "application/pdf" || file?.name?.endsWith(".pdf");

// Mock data for UI testing
const mockHotelData = {
    hotelName: "寵物天堂酒店",
    address: "台北市中正區忠孝東路一段100號",
    intro: "我們提供舒適的住宿環境，專為您的寵物設計，讓他們在您不在時也能享受家的溫暖。",
    phoneNumber: "02-1234-5678",
    secondPhoneNumber: "02-8765-4321",
    email: "hotel@gmail.com",
    whatsapp: "+886912345678",
    operationStart: "08:00",
    operationEnd: "22:00",
    checkinTime: "14:00",
    checkoutTime: "12:00",
    openOnHoliday: "Yes",
    holidayCheckinTime: "14:00",
    holidayCheckoutTime: "12:00",
    legalRepresentative: "王小明",
    businessBranchName: "寵物天堂分店",
    registrationCertNumber: "A123456789",
    businessRegAuthority: "台北市政府",
    operationPeriod: "2020-01-01 至 2030-12-31",
    businessNature: "寵物住宿服務",
    requireBookingTerms: true,
};

// Mock files for testing - using HotelRegis.pdf for both
const mockBusinessRegFile = {
    name: "HotelRegis.pdf",
    type: "application/pdf",
    path: "img/HotelRegis.pdf",
};

const mockAnimalLicenseFile = {
    name: "HotelRegis.pdf",
    type: "application/pdf",
    path: "img/HotelRegis.pdf",
};

const requireBookingTerms = {
    name: "HotelRegis.pdf",
    type: "application/pdf",
    path: "img/HotelRegis.pdf",
};


const SERVICE_OPTIONS = [
    { key: "boarding", labelKey: "ServiceBoarding", unitKey: "UnitPerTime", icon: "/icons/groom.svg" },
    { key: "nailTrim", labelKey: "ServiceNailTrim", unitKey: "UnitPerTime", icon: "/icons/nailTrim.svg" },
    { key: "bathing", labelKey: "ServiceBathing", unitKey: "UnitPerTime", icon: "/icons/bath.svg" },
    { key: "shaving", labelKey: "ServiceShaving", unitKey: "UnitPerTime", icon: "/icons/medication.svg" },
    { key: "playtime", labelKey: "ServicePlaytime", unitKey: "UnitPerMinute", icon: "/icons/play.svg" },
    { key: "pickup", labelKey: "ServicePickup", unitKey: "UnitPerTrip", icon: "/icons/pickup.svg" },
];

// Mock selected services with prices for display
const mockSelectedServices = {
    selectedServices: {
        boarding: true,
        nailTrim: false,
        bathing: true,
        shaving: true,
        playtime: false,
        pickup: true,
    },
    servicePrices: {
        boarding: 500,
        nailTrim: 200,
        bathing: 300,
        shaving: 350,
        playtime: 50,
        pickup: 100,
    },
};

const ANIMAL_TYPES = [
    { key: "dog", labelKey: "AnimalDog", icon: "/icons/dog.svg" },
    { key: "cat", labelKey: "AnimalCat", icon: "/icons/cat.svg" },
    { key: "rabbit", labelKey: "AnimalRabbit", icon: "/icons/rabbit.svg" },
    { key: "bird", labelKey: "AnimalBird", icon: "/icons/bird.svg" },
    { key: "smallAnimal", labelKey: "SmallAnimal", icon: "/icons/hamster.svg" },
    { key: "reptile", labelKey: "AnimalReptile", icon: "/icons/turtle.svg" },
];

const selectedAnimals = ["dog", "cat"];

const ROOM_DATA = {
    roomName: "標準單人房",
    roomQuantity: "5",
    acceptedPetType: "貓咪，狗狗",
    acceptedPetQuantity: "1",
    roomDimensions: "3尺 x 2尺 x 2尺",
    roomPhotos: ["/icons/hotelPaw.png", "/icons/hotelPaw.png", "/icons/hotelPaw.png"],
    roomDesc: "寬敞舒適的單人房間，配備空調及基本設施。",
    weekdayPrice: "$200",
    weekendPrice: "$250",
    freeCancelDeadline: "入住日前 7 天或以上",
    lateCancelFee: "第一晚房費",
};

const YES_NO_PAIRS = [
    [
        { labelKey: "Heating24hLabel", value: "是" },
        { labelKey: "AdditionalFeeLabel", value: "$50" },
    ],
    [
        { labelKey: "PetPhotosVideosLabel", value: "是" },
        { labelKey: "CustomSnacksLabel", value: "否" },
    ],
    [
        { labelKey: "Ventilation24hLabel", value: "是" },
        { labelKey: "DailyDisinfectionLabel", value: "是" },
    ],
    [
        { labelKey: "DailyInteractionLabel", value: "是" },
        { labelKey: "CustomRequestsLabel", value: "否" },
    ],
    [
        { labelKey: "HasWindowLabel", value: "是" },
        { labelKey: "LateCheckoutLabel", value: "是" },
    ],
];

const PROMOTION_DATA = "早鳥優惠 - 適用於提前7日或以上預定的訂單，享9折優惠";

const AdminReview = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useMediaQuery("(max-width:950px)");
    const [previewFile, setPreviewFile] = useState(null);

    const rowLabelSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", mb: 0.5 };
    const rowValueSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" };

    const [formData, setFormData] = useState({
        rejectionReason: "",
    });

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const fileCardStyle = {
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: "#FFFFFF",
        border: "1.5px solid #D1D1D1",
        borderRadius: "10px",
        padding: isMobile ? "16px" : "20px 24px",
        width: "100%",
        boxSizing: "border-box",
        cursor: "pointer",
        transition: "border-color 0.2s ease",
        "&:hover": {
            borderColor: "#31BACD",
        },
    };

    const pageStyle = {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#F5F5F5",
    };

    const contentStyle = {
        flex: "1 0 auto",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
        maxWidth: "1600px",
        width: "100%",
    };

    const ReviewRow = ({ labelKey, value }) => (
        <Box sx={{ padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
            <Typography sx={rowLabelSx}>{t(labelKey)}</Typography>
            <Typography sx={rowValueSx}>{value}</Typography>
        </Box>
    );

    return (
        <div style={pageStyle}>
            <Navbar />
            <div style={contentStyle}>
                <Box sx={{ width: isMobile ? "100%" : "80%", mx: "auto", display: "flex", flexDirection: "column" }}>
                    {/* Heading */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                        <Button
                            onClick={() => navigate("/admin/list-hotels")}
                            sx={{
                                minWidth: "auto",
                                width: isMobile ? "36px" : "44px",
                                height: isMobile ? "36px" : "44px",
                                borderRadius: "50%",
                                backgroundColor: "#31BACD",
                                color: "#fff",
                                padding: 0,
                                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                            }}
                        >
                            <ArrowLeft size={isMobile ? 18 : 22} />
                        </Button>
                        <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                            {t('ReviewInfoTitle')}
                        </Typography>
                    </Box>

                    <Box sx={{ width: "100%", }}>
                        {/* Animal Types Section */}
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 2, mb: 3 }}>
                            <Box>
                                <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mb: 1 }}>
                                    {t('HotelInfoStep1Title')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Animal type grid */}
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 2, width: "100%", mb: 4 }}>
                            {ANIMAL_TYPES.map((animal) => {
                                const selected = selectedAnimals.includes(animal.key);
                                if (!selected) return null;
                                return (
                                    <Box
                                        key={animal.key}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            backgroundColor: "#FFFFFF",
                                            border: "2px solid #31BACD",
                                            borderRadius: "10px",
                                            padding: "20px",
                                            height: isMobile ? "70px" : "90px",
                                            cursor: "pointer",
                                            transition: "border-color 0.2s ease",
                                        }}
                                    >
                                        <img src={animal.icon} alt={t(animal.labelKey)} style={{ width: "32px", height: "32px" }} />
                                        <Typography sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "500", color: "#000" }}>
                                            {t(animal.labelKey)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Basic Info */}
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mb: 2 }}>
                                {t('HotelInfoStep2Title')}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('HotelNameLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.hotelName}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('HotelAddressLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.address}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('HotelIntroLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.intro}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Contact Info */}
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mb: 2 }}>
                                {t('HotelInfoStep3Title')}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('PhoneNumberLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.phoneNumber}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('SecondPhoneNumberLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.secondPhoneNumber}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('HotelEmailLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.email}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('WhatsappLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.whatsapp}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mb: 2 }}>
                                {t('HotelInfoStep4Title')}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('OperationHoursLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.operationStart} - {mockHotelData.operationEnd}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('CheckinCheckoutLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.checkinTime} - {mockHotelData.checkoutTime}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('OpenOnHolidayLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.openOnHoliday ? t('Yes') : t('No')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.holidayCheckinTime} - {mockHotelData.holidayCheckoutTime}</Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* License Verification Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mb: 2 }}>
                                {t('LicenseVerificationTitle')}
                            </Typography>

                            {/* Business Registration Certificate */}
                            <Box sx={{ mb: 3 }}>
                                <Typography sx={rowLabelSx}>{t('BusinessRegCertLabel')}</Typography>
                                {mockBusinessRegFile && (
                                    <Box
                                        sx={fileCardStyle}
                                        onClick={() => setPreviewFile(mockBusinessRegFile)}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? "40px" : "48px",
                                                height: isMobile ? "40px" : "48px",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "#F5F5F5",
                                                border: "1px solid #E0E0E0",
                                            }}
                                        >
                                            {isPdf(mockBusinessRegFile) ? (
                                                <FileText size={22} color="#999" />
                                            ) : (
                                                <Box
                                                    component="img"
                                                    src={mockBusinessRegFile.path}
                                                    alt={mockBusinessRegFile.name}
                                                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                                                {mockBusinessRegFile.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                                                {t('ClickToPreview')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* License Details */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('LegalRepresentativeLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.legalRepresentative}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('BusinessBranchNameLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.businessBranchName}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('RegistrationCertNumberLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.registrationCertNumber}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('BusinessRegAuthorityLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.businessRegAuthority}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('OperationPeriodLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.operationPeriod}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('BusinessNatureLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.businessNature}</Typography>
                                </Box>
                            </Box>

                            {/* Animal License File */}
                            <Box sx={{ mt: 3 }}>
                                <Typography sx={rowLabelSx}>{t('AnimalLicenseLabel')}</Typography>
                                {mockAnimalLicenseFile && (
                                    <Box
                                        sx={fileCardStyle}
                                        onClick={() => setPreviewFile(mockAnimalLicenseFile)}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? "40px" : "48px",
                                                height: isMobile ? "40px" : "48px",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "#F5F5F5",
                                                border: "1px solid #E0E0E0",
                                            }}
                                        >
                                            {isPdf(mockAnimalLicenseFile) ? (
                                                <FileText size={22} color="#999" />
                                            ) : (
                                                <Box
                                                    component="img"
                                                    src={mockAnimalLicenseFile.path}
                                                    alt={mockAnimalLicenseFile.name}
                                                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                                                {mockAnimalLicenseFile.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                                                {t('ClickToPreview')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Room Info */}
                        <Box sx={{ mt: 4 }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", display: "flex", alignItems: "center", gap: 1 }}>
                                {t('RoomInfoStep1Title')}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('RoomNameLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.roomName}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('RoomQuantityLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.roomQuantity}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('AcceptedPetTypeLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.acceptedPetType}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('AcceptedPetQuantityLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.acceptedPetQuantity}</Typography>
                                </Box>
                            </Box>

                            <ReviewRow labelKey="RoomDimensionsLabel" value={ROOM_DATA.roomDimensions} />

                            <Box sx={{ padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('RoomPhotosLabel')}</Typography>
                                <Box sx={{ display: "flex", gap: 1.5, mb: 0.5 }}>
                                    {ROOM_DATA.roomPhotos.map((src, i) => (
                                        <Box
                                            key={i}
                                            component="img"
                                            src={src}
                                            alt=""
                                            sx={{
                                                width: isMobile ? "70px" : "90px",
                                                height: isMobile ? "70px" : "90px",
                                                borderRadius: "8px",
                                                objectFit: "cover",
                                                border: "1px solid #E0E0E0",
                                                backgroundColor: "#F5F5F5",
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            <ReviewRow labelKey="RoomDescLabel" value={ROOM_DATA.roomDesc} />

                            <Box sx={{ display: "flex", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('WeekdayPriceLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.weekdayPrice}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={rowLabelSx}>{t('WeekendPriceLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{ROOM_DATA.weekendPrice}</Typography>
                                </Box>
                            </Box>

                            <ReviewRow labelKey="FreeCancelDeadlineLabel" value={ROOM_DATA.freeCancelDeadline} />
                            <ReviewRow labelKey="LateCancelFeeLabel" value={ROOM_DATA.lateCancelFee} />

                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", display: "flex", alignItems: "center", gap: 1, mt: 4 }}>
                                {t('RoomInfoStep2Title')}
                            </Typography>
                            {YES_NO_PAIRS.map((pair, i) => (
                                <Box key={i} sx={{ display: "flex", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={rowLabelSx}>{t(pair[0].labelKey)}</Typography>
                                        <Typography sx={rowValueSx}>{pair[0].value}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={rowLabelSx}>{t(pair[1].labelKey)}</Typography>
                                        <Typography sx={rowValueSx}>{pair[1].value}</Typography>
                                    </Box>
                                </Box>
                            ))}

                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mt: 4, display: "flex", alignItems: "center", gap: 1 }}>
                                {t('RoomInfoStep3Title')}
                            </Typography>
                            <Box sx={{ padding: "16px 0" }}>
                                <Typography sx={rowLabelSx}>{t('PromotionQuestionLabel')}</Typography>
                                <Typography sx={rowValueSx}>{PROMOTION_DATA}</Typography>
                            </Box>

                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mt: 4 }}>
                                {t('RoomInfoAddStep1Title')}
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(2, 1fr)", gap: 2, mt: 2 }}>
                                {SERVICE_OPTIONS.map((service) => {
                                    const isSelected = mockSelectedServices.selectedServices[service.key];
                                    if (!isSelected) return null;

                                    return (
                                        <Box
                                            key={service.key}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                                backgroundColor: "#FFFFFF",
                                                border: "1.5px solid #D1D1D1",
                                                borderRadius: "10px",
                                                padding: "16px",
                                                boxSizing: "border-box",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={service.icon}
                                                alt={t(service.labelKey)}
                                                sx={{ width: "40px", height: "40px" }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                                                    {t(service.labelKey)}
                                                </Typography>
                                                <Typography sx={{ fontSize: "12px", color: "#999", mt: 0.5 }}>
                                                    HK${mockSelectedServices.servicePrices[service.key]} / {t(service.unitKey)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", mt: 4 }}>
                                {t('RoomInfoAddStep2Title')}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography sx={rowLabelSx}>{t('RequireBookingTermsLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{mockHotelData.requireBookingTerms ? t('Yes') : t('No')}</Typography>
                                </Box>
                                {requireBookingTerms && (
                                    <Box
                                        sx={fileCardStyle}
                                        onClick={() => setPreviewFile(requireBookingTerms)}
                                    >
                                        <Box
                                            sx={{
                                                width: isMobile ? "40px" : "48px",
                                                height: isMobile ? "40px" : "48px",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "#F5F5F5",
                                                border: "1px solid #E0E0E0",
                                            }}
                                        >
                                            {isPdf(requireBookingTerms) ? (
                                                <FileText size={22} color="#999" />
                                            ) : (
                                                <Box
                                                    component="img"
                                                    src={requireBookingTerms.path}
                                                    alt={requireBookingTerms.name}
                                                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                                                {requireBookingTerms.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                                                {t('ClickToPreview')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", display: "flex", alignItems: "center", gap: 1, mt: 4 }}>
                                {t('ReviewBankDetailLabel')}
                            </Typography>
                            <Box sx={{ width: "100%" }}>
                                <ReviewRow labelKey="AccountNameRowLabel" value="jorgeuscak" />
                                <ReviewRow labelKey="BankNameRowLabel" value="渣打銀行" />
                                <ReviewRow labelKey="BranchCodeRowLabel" value="026" />
                                <ReviewRow labelKey="AccountNumberRowLabel" value="1234567" />
                                <ReviewRow labelKey="StreetAddressLabel" value="旺角彌敦道，招牌 82號 大厦" />
                                <ReviewRow labelKey="RegionLabel" value="Kowloon" />
                                <ReviewRow labelKey="CityLabel" value="Shek Kip Mae" />
                                <ReviewRow labelKey="PostalCodeLabel" value="NA" />
                            </Box>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#31BACD", display: "flex", alignItems: "center", gap: 1, mt: 4 }}>
                                {t('RejectionReasonLabel')}
                                <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                                    {t('PleaseFillRejectionReason')} <span style={{ color: "red" }}>*</span><span> )</span>
                                </Box>
                            </Typography> 
                            <Box>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={formData.rejectionReason}
                                    placeholder="License is expired, please provide a valid license."
                                    onChange={(e) => handleInputChange("rejectionReason", e.target.value)}
                                    InputProps={{
                                        sx: {
                                            bgcolor: "white",
                                            borderRadius: "10px",
                                            height: isMobile ? "45px" : "56px",
                                            fontSize: isMobile ? "14px" : "16px",
                                            mt: 2,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>


                        {/* Action Buttons */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: isMobile ? "space-between" : "flex-end", gap: 2, width: "100%", mt: 4 }}>
                            <Button
                                variant="contained"
                                onClick={() => console.log("Reject")}
                                disabled={!formData.rejectionReason.trim()}
                                sx={{
                                    backgroundColor: "#FF6B6B",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    textTransform: "none",
                                    fontSize: isMobile ? "14px" : "16px",
                                    padding: "10px 32px",
                                    width: isMobile ? "45%" : "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    "&:hover": { opacity: 0.85, backgroundColor: "#FF5252" },
                                }}
                            >
                                <XCircle size={18} />
                                {t('Reject')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => console.log("Approve")}
                                sx={{
                                    backgroundColor: "#31BACD",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    textTransform: "none",
                                    fontSize: isMobile ? "14px" : "16px",
                                    padding: "10px 32px",
                                    width: isMobile ? "45%" : "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                                }}
                            >
                                <CheckCircle size={18} />
                                {t('Approve')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </div>
            <Footer />

            {/* PDF Preview Dialog */}
            <Dialog
                open={!!previewFile}
                onClose={() => setPreviewFile(null)}
                fullWidth
                maxWidth="md"
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
                            {previewFile?.name}
                        </Typography>
                        <Button
                            onClick={() => setPreviewFile(null)}
                            sx={{ minWidth: "auto", padding: "6px", color: "#666" }}
                        >
                            <X size={20} />
                        </Button>
                    </Box>
                    {isPdf(previewFile) ? (
                        <iframe
                            src={previewFile?.path}
                            style={{ width: "100%", height: "600px", border: "1px solid #E0E0E0", borderRadius: "8px" }}
                            title="PDF Preview"
                        />
                    ) : (
                        <Box
                            component="img"
                            src={previewFile?.path}
                            alt={previewFile?.name}
                            sx={{ width: "100%", borderRadius: "8px" }}
                        />
                    )}
                </Box>
            </Dialog>
        </div>
    );
};

export default AdminReview;

